import { Component, OnInit, Input, OnChanges, SimpleChanges, HostListener, ViewChild, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlanService, Plan } from '../services/plan.service';
import { ObjektStrukturService, Sektion } from '../services/objekt-struktur.service';
import { DokumenteService, FileUpload } from '../services/dokumente.service';
import { StandortService, Standort } from '../services/standort.service';
import { KundeService, Kunde } from '../services/kunde.service';
import { environment } from '../../environments/environment';
import { InteractivePlanComponent } from '../components/interactive-plan/interactive-plan.component';

interface DropdownData {
  sektionen?: Sektion[];
  standorte?: Standort[];
  clients?: Kunde[];
  uploads?: FileUpload[];
}

export interface RoomIndicator {
  id: string;
  name: string;
  current_value: string;
  einheit: string;
  schwellenwert: number;
  status: number;
  room_id: string;
  indikator_id: string;
  bemerkung?: string;
  qr_code?: string;
  mehr_weniger: number;
  indikator_number?: number;
  created_at?: string;
  updated_at?: string;
  room?: {
    id: string;
    name: string;
    sektion?: {
      id: string;
      name: string;
      gebaeude?: {
        id: string;
        name: string;
      };
    };
  };
  indikator?: {
    id: string;
    name: string;
    symbol_id?: string;
  };
}

interface PlacedIndicator {
  id: string;
  indicator: RoomIndicator;
  x: number; // Position relative to image (0-100%)
  y: number; // Position relative to image (0-100%)
  isDragging?: boolean;
}

@Component({
  selector: 'app-plan-edit',
  templateUrl: './plan-edit.component.html',
  styleUrls: ['./plan-edit.component.scss']
})
export class PlanEditComponent implements OnInit, OnChanges {
  @Input() planId?: string;
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;
  @ViewChild('interactivePlan') interactivePlanComponent?: InteractivePlanComponent;

  planForm!: UntypedFormGroup;
  plan?: Plan;
  isEditMode = false;
  isLoading = true;
  isSaving = false;
  dropdowns?: DropdownData;
  roomIndicators?: RoomIndicator[];
  
  // Current context
  currentStandortId?: string;
  currentClientId?: string;
  currentStandort?: Standort;
  currentClient?: Kunde;
  
  // File dropdown state
  isFileDropdownOpen = false;
  selectedUpload?: FileUpload;
  
  // Plan dropdown state for interactive preview
  isPlanDropdownOpen = false;
  selectedPreviewPlan?: Plan;
  availablePlans: Plan[] = [];
  
  // Preview image state
  isPreviewImageLoading = false;
  previewImageLoadError = false;
  
  // Zoom state
  zoomLevel = 100;
  minZoom = 25;
  maxZoom = 300;
  
  // Drag and drop state
  placedIndicators: PlacedIndicator[] = [];
  draggedIndicator: RoomIndicator | null = null;
  isDragging = false;
  
  // Tooltip state
  showTooltip = false;
  tooltipX = 0;
  tooltipY = 0;
  tooltipIndicator: PlacedIndicator | null = null;
  
  // Image preview state
  showImagePreview = false;
  isImageLoading = false;
  imageLoadError = false;

  constructor(
    private fb: UntypedFormBuilder,
    private planService: PlanService,
    private objektStrukturService: ObjektStrukturService,
    private dokumenteService: DokumenteService,
    private standortService: StandortService,
    private kundeService: KundeService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadDropdowns();
    this.loadPlan();
    this.loadAvailablePlans();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planId'] && !changes['planId'].firstChange) {
      this.loadPlan();
    }
  }

  initForm(): void {
    this.planForm = this.fb.group({
      bezeichnung: ['', Validators.required],
      sektion_id: ['', Validators.required],
      standort_id: [''],
      client_id: [''],
      upload_id: [''],
      active: [true],
      anmerkungen: ['']
    });
  }

  loadDropdowns(): void {
    // Get standortId from query parameters
    this.route.queryParams.subscribe(params => {
      this.currentStandortId = params['standortId'];
      this.currentClientId = params['clientId'];
      
      if (this.currentStandortId) {
        this.loadStandortData();
        this.loadSektionen();
        this.loadUploads();
      }
      
      if (this.currentClientId) {
        this.loadClientData();
      }
    });
  }

  loadDropdownsForPlan(plan: Plan): void {
    // Load dropdown data for an existing plan
    if (plan.standort_id) {
      this.currentStandortId = plan.standort_id;
      this.loadStandortData();
      this.loadSektionen();
      this.loadUploads();
    }
    
    // Don't load client data separately - it will be loaded from standort's kunde_id
    // The loadStandortData() method will automatically set currentClientId and load client data
  }

  loadStandortData(): void {
    if (!this.currentStandortId) return;
    
    this.standortService.getStandort(this.currentStandortId).subscribe({
      next: (response) => {
        this.currentStandort = response.data;
        this.dropdowns = {
          ...this.dropdowns,
          standorte: [response.data]
        };
        
        // Always use the standort's kunde_id as the client ID
        if (response.data.kunde_id) {
          this.currentClientId = response.data.kunde_id;
          console.log('loadStandortData - Setting client ID from standort:', this.currentClientId);
          
          // Update the form with the client_id from standort
          this.planForm.patchValue({
            client_id: this.currentClientId
          });
          
          // Load client data
          this.loadClientData();
        }
      },
      error: (error) => {
        console.error('Error loading standort:', error);
        this.snackBar.open('Fehler beim Laden des Standorts', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadClientData(): void {
    if (!this.currentClientId) return;
    
    this.kundeService.getKundeByUuid(this.currentClientId).subscribe({
      next: (response) => {
        this.currentClient = response.data;
        this.dropdowns = {
          ...this.dropdowns,
          clients: [response.data]
        };
        
        // Update the form with the client_id
        this.planForm.patchValue({
          client_id: this.currentClientId
        });
      },
      error: (error) => {
        console.error('Error loading client:', error);
        this.snackBar.open('Fehler beim Laden des Kunden', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadSektionen(): void {
    if (!this.currentStandortId) return;
    
    this.objektStrukturService.getSektionenByStandort(this.currentStandortId).subscribe({
      next: (response) => {
        console.log('Sektionen response:', response);
        this.dropdowns = {
          ...this.dropdowns,
          sektionen: response.data || []
        };
      },
      error: (error) => {
        console.error('Error loading sektionen:', error);
        this.snackBar.open('Fehler beim Laden der Sektionen', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadUploads(): void {
    if (!this.currentStandortId) return;
    
    this.dokumenteService.getStandortDokumente(this.currentStandortId, 1, 100).subscribe({
      next: (response) => {
        // Handle both array and object response types
        let uploads: FileUpload[] = [];
        if (Array.isArray(response.data)) {
          uploads = response.data;
        } else if (typeof response.data === 'object') {
          // Flatten grouped data
          uploads = Object.values(response.data).flat();
        }
        
        this.dropdowns = {
          ...this.dropdowns,
          uploads: uploads
        };
        
        // If we have a plan with upload_id but no selectedUpload yet, set it now
        if (this.plan?.upload_id && !this.selectedUpload) {
          const uploadIdNum = parseInt(this.plan.upload_id.toString(), 10);
          this.selectedUpload = uploads.find(u => u.id === uploadIdNum);
        }
      },
      error: (error) => {
        console.error('Error loading uploads:', error);
        this.snackBar.open('Fehler beim Laden der Dateien', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadPlan(): void {
    // Get plan ID from route parameters or input
    let planId = this.planId;
    
    if (!planId) {
      // Try to get from route parameters synchronously
      planId = this.route.snapshot.params['id'];
    }
    
    this.loadPlanById(planId);
  }

  loadPlanById(planId: string | undefined): void {
    if (!planId || planId === 'new') {
      this.isLoading = false;
      this.isEditMode = true;
      this.planForm.enable();
      
      // Handle query parameters for new plan creation
      this.route.queryParams.subscribe(params => {
        if (params['standortId']) {
          this.currentStandortId = params['standortId'];
          this.planForm.patchValue({
            standort_id: params['standortId']
          });
          // Load standort data to get the client ID automatically
          this.loadStandortData();
        }
        if (params['sektionId']) {
          this.planForm.patchValue({
            sektion_id: params['sektionId']
          });
        }
        // Don't set clientId from params - it should come from standort's kunde_id
        // The loadStandortData() method will handle setting the client_id
      });
      
      return;
    }

    this.isLoading = true;
    this.planService.getPlan(planId).subscribe({
      next: (plan) => {
        this.plan = plan;
        
        // Set current context from the loaded plan
        this.currentStandortId = plan.standort_id;
        this.currentClientId = plan.client_id;
        
        // Load dropdown data for the plan's standort
        this.loadDropdownsForPlan(plan);
        
        this.planForm.patchValue({
          bezeichnung: plan.bezeichnung,
          sektion_id: plan.sektion_id,
          standort_id: plan.standort_id,
          client_id: plan.client_id,
          upload_id: plan.upload_id,
          active: plan.active,
          anmerkungen: plan.anmerkungen
        });
        this.planForm.disable(); // Start in view mode
        
        // Set selected upload for existing plans
        if (plan.upload_id && this.dropdowns?.uploads) {
          const uploadIdNum = parseInt(plan.upload_id.toString(), 10);
          this.selectedUpload = this.dropdowns.uploads.find(u => u.id === uploadIdNum);
        }
        
        this.loadRoomIndicators();
        this.isLoading = false;
        
        // Request signed URL for the plan image if it's an image file
        const selectedUpload = this.getSelectedUpload();
        if (selectedUpload && this.isImageFile(selectedUpload)) {
          this.requestSignedUrl(selectedUpload.id as number, false);
        }
      },
      error: (error) => {
        console.error('Error loading plan:', error);
        this.snackBar.open('Fehler beim Laden des Plans', 'Schließen', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  loadRoomIndicators(): void {
    // Load room indicators for this plan's standort
    if (!this.currentStandortId) {
      console.warn('No standort ID available for loading room indicators');
      this.roomIndicators = [];
      return;
    }

    this.objektStrukturService.getRoomIndikatorenByStandort(this.currentStandortId, 200).subscribe({
      next: (response: any) => {
        // Handle both array response and paginated response
        if (Array.isArray(response)) {
          this.roomIndicators = response;
        } else if (response.data && Array.isArray(response.data)) {
          this.roomIndicators = response.data;
        } else {
          this.roomIndicators = [];
        }
        console.log('Room indicators loaded:', this.roomIndicators);
      },
      error: (error) => {
        console.error('Error loading room indicators:', error);
        this.snackBar.open('Fehler beim Laden der Raum-Indikatoren', 'Schließen', { duration: 3000 });
        this.roomIndicators = [];
      }
    });
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (this.isEditMode) {
      this.planForm.enable();
    } else {
      this.planForm.disable();
    }
  }

  savePlan(): void {
    if (this.planForm.invalid) {
      return;
    }

    this.isSaving = true;
    const planData = this.planForm.value;

    if (this.plan) {
      // Update existing plan
      this.planService.updatePlan(this.plan.id, planData).subscribe({
        next: (updatedPlan) => {
          this.plan = updatedPlan;
          this.isEditMode = false;
          this.planForm.disable();
          this.snackBar.open('Plan erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
          this.isSaving = false;
        },
        error: (error) => {
          console.error('Error updating plan:', error);
          this.snackBar.open('Fehler beim Aktualisieren des Plans', 'Schließen', { duration: 3000 });
          this.isSaving = false;
        }
      });
    } else {
      // Create new plan
      this.planService.createPlan(planData).subscribe({
        next: (newPlan) => {
          this.plan = newPlan;
          this.isEditMode = false;
          this.planForm.disable();
          this.snackBar.open('Plan erfolgreich erstellt', 'Schließen', { duration: 3000 });
          this.isSaving = false;
          
          // Navigate to the new plan
          if (!this.isEmbedded) {
            this.router.navigate(['/plan', newPlan.id]);
          }
        },
        error: (error) => {
          console.error('Error creating plan:', error);
          this.snackBar.open('Fehler beim Erstellen des Plans', 'Schließen', { duration: 3000 });
          this.isSaving = false;
        }
      });
    }
  }

  cancelEdit(): void {
    if (this.plan) {
      this.planForm.patchValue({
        bezeichnung: this.plan.bezeichnung,
        sektion_id: this.plan.sektion_id,
        standort_id: this.plan.standort_id,
        client_id: this.plan.client_id,
        upload_id: this.plan.upload_id,
        active: this.plan.active,
        anmerkungen: this.plan.anmerkungen
      });
      this.isEditMode = false;
      this.planForm.disable();
    } else {
      this.goBack();
    }
  }

  deletePlan(): void {
    if (!this.plan) return;

    if (confirm(`Möchten Sie den Plan "${this.plan.bezeichnung}" wirklich löschen?`)) {
      this.planService.deletePlan(this.plan.id).subscribe({
        next: () => {
          this.snackBar.open('Plan erfolgreich gelöscht', 'Schließen', { duration: 3000 });
          this.goBack();
        },
        error: (error) => {
          console.error('Error deleting plan:', error);
          this.snackBar.open('Fehler beim Löschen des Plans', 'Schließen', { duration: 3000 });
        }
      });
    }
  }

  goBack(): void {
    // Check if we have return context from query parameters
    this.route.queryParams.subscribe(params => {
      const returnTo = params['returnTo'];
      const tab = params['tab'];
      
      if (returnTo === 'standort-edit' && this.currentStandortId) {
        // Navigate back to standort-edit with specific tab
        this.router.navigate(['/standort-edit', this.currentStandortId], { 
          queryParams: { tab: tab || 'plaene' } 
        });
        return;
      }
      
      // Case 1: Embedded in standort-edit context
      if (this.isEmbedded && this.parentContext === 'standort-edit') {
        return; // Stay in same context
      }
      
      // Case 2: Plan has standort - navigate to standort-edit
      if (this.plan?.standort_id) {
        this.router.navigate(['/standort-edit', this.plan.standort_id]);
        return;
      }
      
      // Case 3: Creating new plan - use standortId from query parameters
      if (this.currentStandortId) {
        this.router.navigate(['/standort-edit', this.currentStandortId]);
        return;
      }
      
      // Case 4: Fallback to plan list
      this.router.navigate(['/plan']);
    });
  }

  downloadPlan(): void {
    if (this.plan?.upload) {
      // Implement plan download functionality
      this.snackBar.open('Download gestartet...', 'Schließen', { duration: 2000 });
    }
  }

  uploadPlan(): void {
    // Implement plan upload functionality
    this.snackBar.open('Plan-Upload-Funktion wird implementiert...', 'Schließen', { duration: 2000 });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.planForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getIndicatorStatusClass(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    
    if (value > threshold) {
      return 'text-red-600';
    } else if (value > threshold * 0.8) {
      return 'text-yellow-600';
    } else {
      return 'text-green-600';
    }
  }

  getIndicatorStatusClasses(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    
    // Check if mehr_weniger is 1 (more is better) or 0 (less is better)
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isMoreBetter) {
      // For "more is better" indicators
      if (value < threshold * 0.8) {
        return 'bg-red-50 text-red-700 ring-red-600/20';
      } else if (value < threshold) {
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';
      } else {
        return 'bg-green-50 text-green-700 ring-green-600/20';
      }
    } else {
      // For "less is better" indicators
      if (value > threshold) {
        return 'bg-red-50 text-red-700 ring-red-600/20';
      } else if (value > threshold * 0.8) {
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';
      } else {
        return 'bg-green-50 text-green-700 ring-green-600/20';
      }
    }
  }

  getIndicatorIcon(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isMoreBetter) {
      // For "more is better" indicators
      if (value < threshold * 0.8) {
        return 'warning';
      } else if (value < threshold) {
        return 'info';
      } else {
        return 'check_circle';
      }
    } else {
      // For "less is better" indicators
      if (value > threshold) {
        return 'warning';
      } else if (value > threshold * 0.8) {
        return 'info';
      } else {
        return 'check_circle';
      }
    }
  }

  getIndicatorStatusText(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isMoreBetter) {
      // For "more is better" indicators
      if (value < threshold * 0.8) {
        return 'Kritisch';
      } else if (value < threshold) {
        return 'Warnung';
      } else {
        return 'Normal';
      }
    } else {
      // For "less is better" indicators
      if (value > threshold) {
        return 'Kritisch';
      } else if (value > threshold * 0.8) {
        return 'Warnung';
      } else {
        return 'Normal';
      }
    }
  }

  editRoomIndicator(indicator: RoomIndicator): void {
    this.snackBar.open(`Raum-Indikator "${indicator.name}" bearbeiten`, 'Schließen', { duration: 3000 });
  }

  getStandortName(standortId: string): string {
    if (!standortId || !this.dropdowns?.standorte) return '';
    const standort = this.dropdowns.standorte.find(s => s.id === standortId);
    return standort ? standort.name : '';
  }

  getClientName(clientId: string): string {
    if (!clientId || !this.dropdowns?.clients) return '';
    
    const client = this.dropdowns.clients.find(c => {
      // Safety check to prevent undefined errors
      if (!c) return false;
      return (c.uuid && c.uuid === clientId) || (c.id && c.id.toString() === clientId);
    });
    
    return client ? client.suchbegriff : '';
  }

  getUploadName(uploadId: string): string {
    if (!uploadId || !this.dropdowns?.uploads) return '';
    const uploadIdNum = parseInt(uploadId, 10);
    const upload = this.dropdowns.uploads.find(u => u.id === uploadIdNum);
    return upload ? (upload.original_filename || upload.filename || `Upload ${upload.id}`) : '';
  }

  getUploadDisplayText(upload: FileUpload): string {
    const filename = upload.original_filename || upload.filename || 'Unbekannte Datei';
    const fileSize = this.formatFileSize(upload.file_size || 0);
    const uploadDate = this.formatDate(upload.uploadDate || '');
    return `${filename} (${fileSize}) - ${uploadDate}`;
  }

  getSelectedUpload(): FileUpload | null {
    const uploadId = this.planForm.get('upload_id')?.value;
    if (!uploadId || !this.dropdowns?.uploads) return null;
    const uploadIdNum = parseInt(uploadId, 10);
    return this.dropdowns.uploads.find(u => u.id === uploadIdNum) || null;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  getBackButtonTooltip(): string {
    // Case 1: Embedded in standort-edit context
    if (this.isEmbedded && this.parentContext === 'standort-edit') {
      return 'Im Standort-Edit bleiben';
    }
    
    // Case 2: Plan has standort - show standort navigation
    if (this.plan?.standort_id) {
      const standortName = this.getStandortName(this.plan.standort_id);
      return standortName ? `Zurück zu Standort: ${standortName}` : 'Zurück zu Standort';
    }
    
    // Case 3: Creating new plan - show standort navigation
    if (this.currentStandortId) {
      const standortName = this.currentStandort?.name || 'Standort';
      return `Zurück zu Standort: ${standortName}`;
    }
    
    // Case 4: Fallback to plan list
    return 'Zurück zu Pläne';
  }

  // Drag and Drop Methods for Interactive Plan Preview
  onDragStart(event: DragEvent, indicator: RoomIndicator): void {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', JSON.stringify(indicator));
      event.dataTransfer.effectAllowed = 'copy';
      this.draggedIndicator = indicator;
    }
  }

  onImageDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onImageDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer && this.draggedIndicator) {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      
      // Create a new placed indicator
      const placedIndicator: PlacedIndicator = {
        id: `placed_${Date.now()}_${this.draggedIndicator.id}`,
        indicator: this.draggedIndicator,
        x: Math.max(0, Math.min(100, x)), // Clamp between 0-100%
        y: Math.max(0, Math.min(100, y))  // Clamp between 0-100%
      };
      
      this.placedIndicators.push(placedIndicator);
      this.draggedIndicator = null;
      
      this.snackBar.open(`Indikator "${placedIndicator.indicator.name}" wurde platziert`, 'Schließen', { duration: 2000 });
    }
  }

  onPlacedIndicatorDragStart(event: DragEvent, placedIndicator: PlacedIndicator): void {
    event.stopPropagation();
    this.isDragging = true;
    placedIndicator.isDragging = true;
  }

  onPlacedIndicatorDragEnd(event: DragEvent, placedIndicator: PlacedIndicator): void {
    this.isDragging = false;
    placedIndicator.isDragging = false;
  }

  onPlacedIndicatorMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const draggingIndicator = this.placedIndicators.find(pi => pi.isDragging);
      if (draggingIndicator) {
        const imageContainer = (event.target as HTMLElement).closest('.image-container');
        if (imageContainer) {
          const rect = imageContainer.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          
          draggingIndicator.x = Math.max(0, Math.min(100, x));
          draggingIndicator.y = Math.max(0, Math.min(100, y));
        }
      }
    }
  }

  removePlacedIndicator(placedIndicator: PlacedIndicator): void {
    const index = this.placedIndicators.findIndex(pi => pi.id === placedIndicator.id);
    if (index > -1) {
      this.placedIndicators.splice(index, 1);
      this.snackBar.open(`Indikator "${placedIndicator.indicator.name}" wurde entfernt`, 'Schließen', { duration: 2000 });
    }
  }

  // Tooltip methods
  showIndicatorTooltip(event: MouseEvent, placedIndicator: PlacedIndicator): void {
    this.tooltipIndicator = placedIndicator;
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY;
    this.showTooltip = true;
  }

  hideIndicatorTooltip(): void {
    this.showTooltip = false;
    this.tooltipIndicator = null;
  }

  // File dropdown methods
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isFileDropdownOpen) {
      this.isFileDropdownOpen = false;
    }
    if (this.isPlanDropdownOpen) {
      this.isPlanDropdownOpen = false;
    }
  }

  toggleFileDropdown(event: Event): void {
    event.stopPropagation();
    this.isFileDropdownOpen = !this.isFileDropdownOpen;
  }

  selectUpload(upload: FileUpload): void {
    this.selectedUpload = upload;
    this.planForm.patchValue({ upload_id: upload.id });
    this.isFileDropdownOpen = false;
    
    // Reset image preview state when selecting a new file
    this.showImagePreview = false;
    this.isImageLoading = false;
    this.imageLoadError = false;
  }

  getUploadSecondaryText(upload: FileUpload): string {
    const parts: string[] = [];
    if (upload.mime_type) {
      parts.push(this.getDisplayCategory(upload.mime_type));
    }
    if (upload.created_at) {
      parts.push(this.formatDate(upload.created_at));
    }
    return parts.join(' • ');
  }

  getDisplayCategory(mimeType: string): string {
    if (mimeType?.startsWith('image/')) {
      return 'Bild';
    } else if (mimeType === 'application/pdf') {
      return 'PDF';
    } else if (mimeType?.includes('word') || mimeType?.includes('document')) {
      return 'Dokument';
    } else if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) {
      return 'Kalkulation';
    } else {
      return 'Dokument';
    }
  }

  getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  // Image Preview Methods
  isImageFile(upload: FileUpload | null): boolean {
    if (!upload || !upload.mime_type) return false;
    return upload.mime_type.startsWith('image/');
  }

  getFileUrl(upload: FileUpload | null): string {
    if (!upload) return '';
    // Prefer pre-signed URL for images to avoid JWT on <img> requests.
    const id = upload.id as number;
    if (this.isImageFile(upload)) {
      const cache = (this as any)._signedUrlCache || {};
      if (cache[id]) return cache[id];
      this.requestSignedUrl(id, false);
      return '';
    }
    return `${environment.apiUrl}/fileupload/${id}/download`;
  }

  private requestSignedUrl(id: number, download: boolean): void {
    const self: any = this as any;
    if (!self._signedUrlCache) self._signedUrlCache = {};
    this.dokumenteService.signDokumentUrl(id, 300, download).subscribe({
      next: (res) => {
        if (res && res.url) {
          // Avoid double /v5 in the final URL when res.url already begins with /v5
          // environment.apiUrl typically ends with /v5
          const base = environment.apiUrl.replace(/\/$/, '').replace(/\/v5$/, '');
          const signedUrl = `${base}${res.url}`;
          self._signedUrlCache[id] = signedUrl;
          
          // Notify the interactive plan component if it exists and this is for the current plan
          if (this.interactivePlanComponent && this.getSelectedUpload()?.id === id) {
            this.interactivePlanComponent.updatePlanImageUrl(signedUrl);
            
            // If the component is not properly initialized, try to reinitialize it
            setTimeout(() => {
              if (this.interactivePlanComponent) {
                this.interactivePlanComponent.reinitialize();
              }
            }, 500);
          }
          
          // Trigger change detection to update the template
          this.cdr.detectChanges();
        }
      },
      error: () => {}
    });
  }

  toggleImagePreview(): void {
    this.showImagePreview = !this.showImagePreview;
    if (this.showImagePreview) {
      this.isImageLoading = true;
      this.imageLoadError = false;
    }
  }

  onImageLoad(event: Event): void {
    this.isImageLoading = false;
    this.imageLoadError = false;
  }

  onImageError(event: Event): void {
    this.isImageLoading = false;
    this.imageLoadError = true;
    console.error('Error loading image:', event);
  }

  retryImageLoad(): void {
    this.imageLoadError = false;
    this.isImageLoading = true;
    // The image will automatically retry loading when the error state is cleared
  }

  // Plan dropdown methods for interactive preview
  loadAvailablePlans(): void {
    if (!this.currentStandortId) return;
    
    this.planService.getPlansByStandort(this.currentStandortId).subscribe({
      next: (response: any) => {
        // Filter for active plans only
        this.availablePlans = (response.data || response || []).filter((plan: Plan) => plan.active);
        
        // Set current plan as selected if it exists and is active
        if (this.plan && this.plan.active) {
          this.selectedPreviewPlan = this.plan;
        } else if (this.availablePlans.length > 0) {
          this.selectedPreviewPlan = this.availablePlans[0];
        }
      },
      error: (error) => {
        console.error('Error loading available plans:', error);
        this.availablePlans = [];
      }
    });
  }

  togglePlanDropdown(event: Event): void {
    event.stopPropagation();
    this.isPlanDropdownOpen = !this.isPlanDropdownOpen;
  }

  selectPreviewPlan(plan: Plan): void {
    this.selectedPreviewPlan = plan;
    this.isPlanDropdownOpen = false;
    
    // Reset preview image state
    this.isPreviewImageLoading = false;
    this.previewImageLoadError = false;
    
    // Load room indicators for the selected plan's sektion
    if (plan.sektion_id) {
      this.loadRoomIndicatorsForSektion(plan.sektion_id);
    }
  }

  loadRoomIndicatorsForSektion(sektionId: string): void {
    // Load all room indicators for the standort and filter by sektion
    if (!this.currentStandortId) return;
    
    this.objektStrukturService.getRoomIndikatorenByStandort(this.currentStandortId, 200).subscribe({
      next: (response: any) => {
        // Handle both array response and paginated response
        let allIndicators: RoomIndicator[] = [];
        if (Array.isArray(response)) {
          allIndicators = response;
        } else if (response.data && Array.isArray(response.data)) {
          allIndicators = response.data;
        }
        
        // Filter by sektion and only show active indicators
        this.roomIndicators = allIndicators.filter(indicator => 
          indicator.room?.sektion?.id === sektionId && indicator.status === 1
        );
      },
      error: (error) => {
        console.error('Error loading room indicators for sektion:', error);
        this.roomIndicators = [];
      }
    });
  }

  printRoomIndicator(indicator: RoomIndicator): void {
    this.snackBar.open(`Drucke Raum-Indikator "${indicator.name}"`, 'Schließen', { duration: 3000 });
  }

  printAllRoomIndicators(): void {
    this.snackBar.open('Drucke alle Raum-Indikatoren', 'Schließen', { duration: 3000 });
  }

  // Preview plan image methods
  getSelectedPreviewPlanUpload(): FileUpload | null {
    if (!this.selectedPreviewPlan?.upload_id || !this.dropdowns?.uploads) return null;
    const uploadIdNum = parseInt(this.selectedPreviewPlan.upload_id.toString(), 10);
    return this.dropdowns.uploads.find(u => u.id === uploadIdNum) || null;
  }

  getPreviewPlanFileUrl(): string {
    const upload = this.getSelectedPreviewPlanUpload();
    if (!upload) return '';
    
    const id = upload.id as number;
    if (this.isImageFile(upload)) {
      const cache = (this as any)._signedUrlCache || {};
      if (cache[id]) return cache[id];
      this.requestSignedUrl(id, false);
      return '';
    }
    return `${environment.apiUrl}/fileupload/${id}/download`;
  }

  onPreviewImageLoad(event: Event): void {
    this.isPreviewImageLoading = false;
    this.previewImageLoadError = false;
  }

  onPreviewImageError(event: Event): void {
    this.isPreviewImageLoading = false;
    this.previewImageLoadError = true;
    console.error('Error loading preview image:', event);
  }

  // Zoom functionality
  zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + 25, this.maxZoom);
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - 25, this.minZoom);
    }
  }

  resetZoom(): void {
    this.zoomLevel = 100;
  }

  getZoomPercentage(): string {
    return `${this.zoomLevel}%`;
  }

  getImageStyle(): any {
    return {
      transform: `scale(${this.zoomLevel / 100})`,
      transformOrigin: 'center center',
      transition: 'transform 0.2s ease-in-out'
    };
  }

  getPlacedIndicatorStyle(placedIndicator: PlacedIndicator): any {
    // Calculate position relative to the image container, accounting for zoom
    const zoomFactor = this.zoomLevel / 100;
    const containerPadding = 16; // p-4 = 16px padding
    
    return {
      left: `${placedIndicator.x}%`,
      top: `${placedIndicator.y}%`,
      transform: 'translate(-50%, -50%)',
      transition: placedIndicator.isDragging ? 'none' : 'all 0.2s ease-in-out'
    };
  }

  savePlanPreview(): void {
    if (!this.plan) {
      this.snackBar.open('Kein Plan geladen', 'Schließen', { duration: 3000 });
      return;
    }
    
    this.snackBar.open(`Plan "${this.plan.bezeichnung}" wird gespeichert...`, 'Schließen', { duration: 2000 });
    // Here you could implement actual save functionality
    // For now, just show a success message
    setTimeout(() => {
      this.snackBar.open('Plan erfolgreich gespeichert', 'Schließen', { duration: 3000 });
    }, 2000);
  }

  // Indicator symbol and status methods
  getIndicatorSymbolUrl(indicator: RoomIndicator): string | null {
    if (!indicator.indikator?.symbol_id) return null;
    const key = (environment as any).indikatorSymbolMap?.[indicator.indikator.symbol_id];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.png`;
  }

  getIndicatorStatusTextSimple(status: number): string {
    return status === 1 ? 'Aktiv' : 'Inaktiv';
  }

  getIndicatorStatusBadgeClasses(status: number): string {
    if (status === 1) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-red-100 text-red-800';
  }

  // Event handlers for the new Konva-based Interactive Plan Component
  onMarkerPlaced(marker: any): void {
    console.log('Marker placed:', marker);
    // Handle marker placement - could save to backend here
  }

  onMarkerMoved(marker: any): void {
    console.log('Marker moved:', marker);
    // Handle marker movement - could save position to backend here
  }

  onMarkerRemoved(markerId: string): void {
    console.log('Marker removed:', markerId);
    // Handle marker removal - could remove from backend here
  }

  onZoomChanged(zoomLevel: number): void {
    console.log('Zoom changed:', zoomLevel);
    this.zoomLevel = zoomLevel;
  }

  onPlanExported(dataUrl: string): void {
    console.log('Plan exported:', dataUrl);
    // Handle plan export - could trigger download here
    const link = document.createElement('a');
    link.download = `plan-${this.plan?.bezeichnung || 'export'}-${new Date().toISOString().split('T')[0]}.png`;
    link.href = dataUrl;
    link.click();
  }

  // Method to get the plan image URL for the interactive plan component
  getPlanImageUrl(): string {
    const selectedUpload = this.getSelectedUpload();
    if (!selectedUpload) return '';
    
    // For images, try to get the signed URL from cache first
    if (this.isImageFile(selectedUpload)) {
      const cache = (this as any)._signedUrlCache || {};
      const cachedUrl = cache[selectedUpload.id as number];
      if (cachedUrl) {
        return cachedUrl;
      }
      // If no cached URL, request one
      this.requestSignedUrl(selectedUpload.id as number, false);
      return '';
    }
    
    // For non-images, return the direct download URL
    return `${environment.apiUrl}/fileupload/${selectedUpload.id}/download`;
  }
}
